import {
  AI_DRAFT_PURPOSES,
  AI_DRAFT_PURPOSE_LABELS,
  AI_SUGGESTION_FIELD_LABELS,
  type AiDraftEmailResponse,
  type AiDraftPurpose,
  type AiFieldSuggestion,
  type AiSuggestFieldsResponse,
  type AiSummaryResponse,
} from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Sparkles, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, CardBody, CardHeader, Field, Select, Textarea, useToast } from '../../components/ui';
import { useLookups } from '../../hooks/useMeta';
import { api, errorMessage } from '../../lib/api';

/** Fields an AI suggestion can be written straight onto the lead. Others stay as text to copy. */
/** Purchase timeframe, budget and value are plain lead fields; interests and ovens need their own screens. */
const APPLIABLE: ReadonlySet<AiFieldSuggestion['field']> = new Set(['purchaseTimeframe', 'budget', 'estimatedValue']);

/**
 * Optional AI help on a lead: a written summary, a first draft email, or suggested field values — each with the
 * evidence the model used, and never applied without a person clicking. Hidden entirely when the server has no
 * AI provider configured.
 */
export function LeadAiPanel({ businessId, leadName }: { businessId: string; leadName: string }) {
  const { meta } = useLookups();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [purpose, setPurpose] = useState<AiDraftPurpose>('follow_up');
  const [summary, setSummary] = useState<AiSummaryResponse | null>(null);
  const [draft, setDraft] = useState<AiDraftEmailResponse | null>(null);
  const [suggestions, setSuggestions] = useState<AiSuggestFieldsResponse | null>(null);
  const onError = (error: unknown) => toast.error(errorMessage(error));

  const enrich = useMutation({
    mutationFn: () => api.post<AiSummaryResponse>(`/api/ai/leads/${businessId}/summary`),
    onSuccess: setSummary,
    onError,
  });
  const write = useMutation({
    mutationFn: () => api.post<AiDraftEmailResponse>(`/api/ai/leads/${businessId}/draft-email`, { purpose }),
    onSuccess: setDraft,
    onError,
  });
  const suggest = useMutation({
    mutationFn: () => api.post<AiSuggestFieldsResponse>(`/api/ai/leads/${businessId}/suggest-fields`),
    onSuccess: setSuggestions,
    onError,
  });
  const apply = useMutation({
    mutationFn: (suggestion: AiFieldSuggestion) => api.patch(`/api/leads/${businessId}`, { [suggestion.field]: suggestion.value }),
    onSuccess: async () => {
      toast.success('Written onto the lead');
      await queryClient.invalidateQueries({ queryKey: ['leads', businessId] });
    },
    onError,
  });

  if (!meta?.integrations.aiConfigured) return null;
  const busy = enrich.isPending || write.isPending || suggest.isPending;

  return (
    <Card>
      <CardHeader title="AI assistant" description="Suggestions only — check them against what you know before using them." actions={<Badge color="violet" size="sm">Experimental</Badge>} />
      <CardBody className="space-y-3 text-[13px]">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" icon={Sparkles} loading={enrich.isPending} disabled={busy && !enrich.isPending} onClick={() => enrich.mutate()}>
            Summarize
          </Button>
          <Select size="sm" className="w-40" aria-label="Draft purpose" value={purpose} onChange={(e) => setPurpose(AI_DRAFT_PURPOSES.find((p) => p === e.target.value) ?? 'follow_up')}>
            {AI_DRAFT_PURPOSES.map((option) => (
              <option key={option} value={option}>
                {AI_DRAFT_PURPOSE_LABELS[option]}
              </option>
            ))}
          </Select>
          <Button size="sm" icon={Wand2} loading={write.isPending} disabled={busy && !write.isPending} onClick={() => write.mutate()}>
            Draft email
          </Button>
          <Button size="sm" loading={suggest.isPending} disabled={busy && !suggest.isPending} onClick={() => suggest.mutate()}>
            Find missing details
          </Button>
        </div>

        {summary && (
          <div className="rounded-md bg-slate-50 p-3">
            <p className="whitespace-pre-wrap text-slate-700">{summary.summary}</p>
            <p className="mt-1 text-xs text-slate-400">
              {summary.model} · {new Date(summary.generatedAt).toLocaleString()}
            </p>
          </div>
        )}

        {draft && (
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <Field label="Subject">
              <Textarea rows={1} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </Field>
            <Field label="Body" hint="Send it from the Email tab (or the composer) once you are happy with it.">
              <Textarea rows={8} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            </Field>
            <Link to={`/leads/${businessId}?tab=emails&compose=1&subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`} className="text-ember-700 hover:underline">
              Open in the email composer
            </Link>
          </div>
        )}

        {suggestions && (
          <div className="space-y-2">
            {suggestions.suggestions.length === 0 && <p className="text-slate-500">Nothing obviously missing from {leadName}.</p>}
            {suggestions.suggestions.map((suggestion) => (
              <div key={suggestion.field} className="rounded-md border border-slate-200 p-2">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800">
                      {AI_SUGGESTION_FIELD_LABELS[suggestion.field]}: <span className="font-normal">{formatSuggestion(suggestion)}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      From {suggestion.evidence.source}
                      {suggestion.evidence.date ? ` (${suggestion.evidence.date})` : ''}: “{suggestion.evidence.quote}”
                    </p>
                    {!APPLIABLE.has(suggestion.field) && <p className="text-xs text-slate-400">Add this on the lead's own tab.</p>}
                  </div>
                  {APPLIABLE.has(suggestion.field) && (
                    <Button size="sm" icon={Check} loading={apply.isPending} onClick={() => apply.mutate(suggestion)}>
                      Apply
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function formatSuggestion(suggestion: AiFieldSuggestion): string {
  const value = suggestion.value;
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).filter(Boolean).join(', ');
  return String(value);
}
